import {
  BadgeCheck,
  ChevronRight,
  Download,
  FileVideo,
  ImageDown,
  Link,
  LoaderCircle,
  Lock,
  Music,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Video,
  Zap
} from 'lucide-react';
import type { FormEvent, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

type PlatformId = 'youtube' | 'tiktok' | 'instagram' | 'threads' | 'facebook' | 'xiaohongshu' | 'pinterest' | 'x' | 'aliexpress';

type DownloadOption = {
  id: string;
  kind: 'MP4' | 'MP3' | 'M4A' | 'GIF 변환' | 'JPG';
  label: string;
  detail: string;
  icon: 'video' | 'music' | 'gif' | 'image';
  directUrl?: string;
  audioUrl?: string;
  directToken?: string;
};

type Feature = {
  icon: 'zap' | 'hd' | 'shield' | 'sparkles' | 'lock' | 'phone' | 'smile' | 'drop';
  title: string;
  text: string;
};

type LegalSection = {
  title: string;
  body?: string[];
  items?: string[];
};

type LegalTab = 'terms' | 'privacy' | 'contact';

type Platform = {
  id: PlatformId;
  brand: string;
  label: string;
  icon: string;
  h1: string;
  subtitle: string;
  placeholder: string;
  orientation: 'landscape' | 'portrait' | 'square';
  contentTitle: string;
  author: string;
  handle: string;
  duration: string;
  date: string;
  verified: boolean;
  options: DownloadOption[];
  features: Feature[];
  steps: string[];
  themeLabel: string;
};

type ExtractedVideo = {
  id: string;
  platform: PlatformId;
  title: string;
  author: string;
  duration: string;
  date: string;
  thumbnail: string;
  originalUrl: string;
  options: DownloadOption[];
  warning?: string;
};

type ExtractResponse = {
  ok: boolean;
  data?: ExtractedVideo;
  error?: string;
};

const API_BASE = import.meta.env.VITE_DOWNLOAD_API_BASE || '';
const OPEN_CHAT_URL = 'https://open.kakao.com/o/g6qzMqPi';

const apiUrl = (path: string) => `${API_BASE}${path}`;

const extensionForOption = (option: DownloadOption) => {
  if (option.kind === 'JPG') return 'jpg';
  if (option.kind === 'MP3') return 'mp3';
  if (option.kind === 'M4A') return 'm4a';
  return 'mp4';
};

const directMediaUrl = (directUrl: string, filename: string, inline = false) => {
  const inlineQuery = inline ? '&inline=1' : '';
  return apiUrl(`/api/direct?url=${encodeURIComponent(directUrl)}&filename=${encodeURIComponent(filename)}${inlineQuery}`);
};

const defaultOptions: DownloadOption[] = [
  {
    id: 'best',
    kind: 'MP4',
    label: '1080p (FHD)',
    detail: '1920x1080',
    icon: 'video'
  },
  {
    id: 'hd720',
    kind: 'MP4',
    label: '720p (HD)',
    detail: '1280x720',
    icon: 'video'
  },
  {
    id: 'audio',
    kind: 'MP3',
    label: '오디오',
    detail: '320kbps',
    icon: 'music'
  }
];

const platforms: Record<PlatformId, Platform> = {
  youtube: {
    id: 'youtube',
    brand: 'YouTube Downloader',
    label: 'YouTube',
    icon: '/assets/platform-icons/youtube-icon.webp',
    h1: '유튜브 영상 다운로드',
    subtitle: '링크를 붙여넣고 빠르게 저장하세요',
    placeholder: 'YouTube 영상 링크를 붙여넣으세요',
    orientation: 'landscape',
    contentTitle: '설계자x트래픽정 YouTube Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: defaultOptions,
    themeLabel: 'red',
    features: [
      { icon: 'zap', title: '빠른 처리', text: '고성능 서버로 링크 분석부터 다운로드까지 빠르게 처리합니다.' },
      { icon: 'hd', title: '고화질 지원', text: '최대 1080p 화질과 다양한 포맷을 지원합니다.' },
      { icon: 'shield', title: '안전한 사용', text: '광고 없는 깔끔한 흐름으로 편하게 이용하세요.' }
    ],
    steps: ['유튜브에서 저장할 영상 링크를 복사합니다.', '입력창에 링크를 붙여넣고 다운로드 버튼을 누릅니다.', '원하는 형식과 화질을 선택하면 영상이 저장됩니다.']
  },
  tiktok: {
    id: 'tiktok',
    brand: 'TikTok Downloader',
    label: 'TikTok',
    icon: '/assets/platform-icons/tiktok-icon.webp',
    h1: '틱톡·Douyin 영상 다운로드',
    subtitle: 'TikTok과 Douyin 링크를 붙여넣고 워터마크 없이 저장하세요',
    placeholder: 'TikTok 또는 Douyin 영상 링크/공유 문구를 붙여넣으세요',
    orientation: 'portrait',
    contentTitle: '설계자x트래픽정 TikTok Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: defaultOptions,
    themeLabel: 'neon',
    features: [
      { icon: 'zap', title: '빠른 처리', text: 'TikTok과 Douyin 링크 분석부터 파일 저장까지 빠르게 처리합니다.' },
      { icon: 'drop', title: '워터마크 없음', text: '깨끗한 영상 파일로 저장할 수 있습니다.' },
      { icon: 'phone', title: '모바일 최적화', text: '모바일 환경에서도 쉽게 다운로드할 수 있습니다.' }
    ],
    steps: ['TikTok 또는 Douyin 앱에서 공유 버튼을 누르고 링크를 복사합니다.', '입력창에 복사한 링크나 공유 문구 전체를 붙여넣고 다운로드를 누릅니다.', '원하는 형식과 화질을 선택하면 영상이 저장됩니다.']
  },
  xiaohongshu: {
    id: 'xiaohongshu',
    brand: 'Rednote Downloader',
    label: 'Rednote',
    icon: '/assets/platform-icons/xiaohongshu-icon.webp',
    h1: '샤오홍슈 영상 다운로드',
    subtitle: 'Rednote와 Xiaohongshu 링크를 붙여넣고 빠르게 저장하세요',
    placeholder: '샤오홍슈 또는 Rednote 링크/공유 문구를 붙여넣으세요',
    orientation: 'portrait',
    contentTitle: '설계자x트래픽정 Rednote Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: defaultOptions,
    themeLabel: 'rednote',
    features: [
      { icon: 'zap', title: '공유 문구 지원', text: '앱에서 복사한 공유 문구 전체를 붙여넣어도 링크를 감지합니다.' },
      { icon: 'hd', title: '원본 파일 저장', text: '공개 노트의 다운로드 가능한 영상 파일을 바로 저장할 수 있습니다.' },
      { icon: 'lock', title: '로그인 불필요', text: '계정 없이 공개 링크만으로 다운로드 흐름을 처리합니다.' }
    ],
    steps: ['샤오홍슈 또는 Rednote 앱에서 공유 버튼을 누르고 링크를 복사합니다.', '입력창에 링크나 공유 문구 전체를 붙여넣고 다운로드를 누릅니다.', '원하는 형식과 화질을 선택하면 영상이 저장됩니다.']
  },
  pinterest: {
    id: 'pinterest',
    brand: 'Pinterest Downloader',
    label: 'Pinterest',
    icon: '/assets/platform-icons/pinterest-icon.webp',
    h1: '핀터레스트 영상 다운로드',
    subtitle: 'Pinterest 핀 링크를 붙여넣고 영상과 썸네일을 저장하세요',
    placeholder: 'Pinterest 또는 pin.it 영상 링크를 붙여넣으세요',
    orientation: 'portrait',
    contentTitle: '설계자x트래픽정 Pinterest Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: defaultOptions,
    themeLabel: 'pinterest',
    features: [
      { icon: 'zap', title: '짧은 링크 지원', text: 'pin.it 공유 링크를 붙여넣어도 최종 핀 URL을 감지합니다.' },
      { icon: 'hd', title: 'MP4 영상 저장', text: '공개 핀에서 제공되는 MP4 영상 후보를 화질별로 정리합니다.' },
      { icon: 'lock', title: '로그인 불필요', text: '공개 핀 링크만으로 분석과 다운로드 흐름을 처리합니다.' }
    ],
    steps: ['Pinterest에서 저장할 영상 핀의 공유 링크를 복사합니다.', '입력창에 Pinterest 또는 pin.it 링크를 붙여넣고 다운로드를 누릅니다.', '원하는 영상 파일이나 썸네일을 선택하면 저장됩니다.']
  },
  instagram: {
    id: 'instagram',
    brand: 'Instagram Downloader',
    label: 'Instagram',
    icon: '/assets/platform-icons/instagram-icon.webp',
    h1: '인스타그램 릴스 다운로드',
    subtitle: '릴스와 영상 링크를 붙여넣고 빠르게 저장하세요',
    placeholder: 'Instagram 릴스 또는 영상 링크를 붙여넣으세요',
    orientation: 'portrait',
    contentTitle: '설계자x트래픽정 Instagram Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: defaultOptions,
    themeLabel: 'gradient',
    features: [
      { icon: 'zap', title: '빠른 처리', text: '릴스 링크를 빠르게 분석하고 다운로드까지 처리합니다.' },
      { icon: 'sparkles', title: '릴스 지원', text: '릴스, 스토리, 피드 영상 등 다양한 콘텐츠를 지원합니다.' },
      { icon: 'smile', title: '간편한 사용', text: '복잡한 과정 없이 몇 번의 클릭으로 저장할 수 있습니다.' }
    ],
    steps: ['인스타그램에서 저장할 릴스 또는 영상 링크를 복사합니다.', '입력창에 링크를 붙여넣고 다운로드 버튼을 누릅니다.', '원하는 형식과 화질을 선택하면 영상이 저장됩니다.']
  },
  threads: {
    id: 'threads',
    brand: 'Threads Downloader',
    label: 'Threads',
    icon: '/assets/platform-icons/threads-icon.webp',
    h1: '스레드 영상 다운로드',
    subtitle: '스레드 링크를 붙여넣고 간편하게 저장하세요',
    placeholder: 'Threads 영상 링크를 붙여넣으세요',
    orientation: 'landscape',
    contentTitle: '설계자x트래픽정 Threads Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: [
      defaultOptions[0],
      defaultOptions[1],
      {
        id: 'gif',
        kind: 'GIF 변환',
        label: 'GIF',
        detail: '최대 480p',
        icon: 'gif'
      }
    ],
    themeLabel: 'mono',
    features: [
      { icon: 'zap', title: '빠른 처리', text: '최적화된 서버로 빠르게 분석하고 다운로드합니다.' },
      { icon: 'sparkles', title: '깔끔한 사용성', text: '단순한 인터페이스로 쉽게 사용할 수 있습니다.' },
      { icon: 'lock', title: '로그인 불필요', text: '계정 없이 바로 사용하고 개인정보를 저장하지 않습니다.' }
    ],
    steps: ['스레드에서 저장할 영상 링크를 복사합니다.', '입력창에 링크를 붙여넣고 다운로드 버튼을 누릅니다.', '원하는 형식과 화질을 선택하면 영상이 저장됩니다.']
  },
  x: {
    id: 'x',
    brand: 'X Downloader',
    label: 'X',
    icon: '/assets/platform-icons/x-icon.webp',
    h1: 'X 영상 다운로드',
    subtitle: 'X · Twitter 링크를 붙여넣고 영상과 썸네일을 저장하세요',
    placeholder: 'X 또는 Twitter 게시물 링크를 붙여넣으세요',
    orientation: 'portrait',
    contentTitle: '설계자x트래픽정 X Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: [],
    themeLabel: 'mono',
    features: [
      { icon: 'hd', title: '화질별 저장', text: '게시물에서 제공하는 MP4 영상을 화질별로 선택합니다.' },
      { icon: 'sparkles', title: '썸네일 저장', text: '영상의 썸네일 이미지도 함께 저장할 수 있습니다.' },
      { icon: 'lock', title: '로그인 불필요', text: '공개 게시물 링크만으로 다운로드합니다.' }
    ],
    steps: ['X에서 영상 게시물의 공유 링크를 복사합니다.', '입력창에 링크를 붙여넣고 다운로드를 누릅니다.', '원하는 영상 화질이나 썸네일을 선택하면 저장됩니다.']
  },
  facebook: {
    id: 'facebook',
    brand: 'Facebook Downloader',
    label: 'Facebook',
    icon: '/assets/platform-icons/facebook-icon.webp',
    h1: '페이스북 영상 다운로드',
    subtitle: '동영상 링크를 붙여넣고 간편하게 저장하세요',
    placeholder: 'Facebook 영상 링크를 붙여넣으세요',
    orientation: 'landscape',
    contentTitle: '설계자x트래픽정 Facebook Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: defaultOptions,
    themeLabel: 'blue',
    features: [
      { icon: 'zap', title: '빠른 처리', text: '링크 분석부터 파일 저장까지 빠르게 처리합니다.' },
      { icon: 'hd', title: '고화질 지원', text: '최대 1080p 화질의 다양한 포맷을 지원합니다.' },
      { icon: 'shield', title: '안전한 사용', text: '계정 없이도 편리하게 사용할 수 있습니다.' }
    ],
    steps: ['페이스북에서 다운로드하고 싶은 영상 링크를 복사합니다.', '입력창에 링크를 붙여넣고 다운로드 버튼을 누릅니다.', '원하는 형식과 화질을 선택하면 영상이 저장됩니다.']
  },
  aliexpress: {
    id: 'aliexpress',
    brand: 'AliExpress Downloader',
    label: 'AliExpress',
    icon: '/assets/platform-icons/aliexpress-icon.svg',
    h1: '알리익스프레스 상품 영상·사진 다운로드',
    subtitle: '상품 링크를 붙여넣고 상품 영상과 원본 사진을 저장하세요',
    placeholder: 'AliExpress 상품 링크 또는 공유 문구를 붙여넣으세요',
    orientation: 'square',
    contentTitle: '설계자x트래픽정 AliExpress Downloader',
    author: '설계자x트래픽정',
    handle: '@seolgyeja',
    duration: '',
    date: '',
    verified: true,
    options: [],
    themeLabel: 'aliexpress',
    features: [
      { icon: 'hd', title: '원본 사진', text: '판매자가 올린 대표 사진과 옵션 사진을 원본 JPG로 저장합니다.' },
      { icon: 'zap', title: '상품 영상', text: '상품에 영상이 있으면 MP4로 함께 저장할 수 있습니다.' },
      { icon: 'lock', title: '로그인 불필요', text: '공개 상품 링크만으로 분석과 다운로드를 처리합니다.' }
    ],
    steps: ['알리익스프레스 앱이나 웹에서 상품 링크를 복사합니다.', '입력창에 링크나 공유 문구 전체를 붙여넣고 다운로드를 누릅니다.', '상품 영상이나 사진을 골라 저장합니다.']
  }
};

const platformList = Object.values(platforms);

const updatedDate = '2026년 6월 6일';

const legalTabs: Array<{ id: LegalTab; label: string }> = [
  { id: 'terms', label: '이용약관' },
  { id: 'privacy', label: '개인정보처리방침' },
  { id: 'contact', label: '문의 및 신고' }
];

const termsSections: LegalSection[] = [
  {
    title: '1. 약관 동의',
    body: [
      '설계자x트래픽정 Video Downloader를 접속하거나 사용하는 경우 본 이용약관에 동의한 것으로 봅니다. 약관에 동의하지 않는 경우 서비스를 사용하지 말아야 합니다.',
      '본 약관은 서비스 화면, 기능, API, 다운로드 흐름 및 관련 자료의 이용에 적용됩니다.'
    ]
  },
  {
    title: '2. 서비스 목적',
    body: [
      '본 서비스는 사용자가 입력한 동영상 또는 이미지 URL을 분석하고, 사용자의 기기로 다운로드할 수 있는 링크 또는 파일 전달 흐름을 제공하는 도구입니다.',
      '본 서비스는 YouTube, TikTok, Rednote, Xiaohongshu, Pinterest, Instagram, Threads, Facebook, X(Twitter) 또는 기타 제3자 플랫폼과 제휴, 후원, 보증 관계에 있지 않습니다. 각 플랫폼명과 상표는 해당 권리자에게 귀속됩니다.'
    ]
  },
  {
    title: '3. 사용자 의무',
    body: ['사용자는 본 서비스를 관련 법령, 제3자 플랫폼 약관, 권리자의 허락 범위 안에서만 사용해야 합니다.'],
    items: [
      '본인이 업로드했거나 권리를 보유한 콘텐츠, 또는 권리자와 법령이 허용한 콘텐츠에 한해 다운로드해야 합니다.',
      '저작권, 상표권, 초상권, 개인정보, 사생활 또는 기타 제3자 권리를 침해하는 방식으로 서비스를 사용해서는 안 됩니다.',
      '비공개 콘텐츠, 유료 콘텐츠, 로그인 또는 접근 제한이 걸린 콘텐츠, DRM이나 보안 장치를 우회하기 위한 목적으로 서비스를 사용해서는 안 됩니다.',
      '과도한 자동화 요청, 악성 코드, 스팸, 서비스 장애를 유발하는 행위는 금지됩니다.',
      '다운로드한 콘텐츠는 개인적이고 비상업적인 목적에서만 사용해야 하며, 재배포나 상업적 이용은 권리자의 허락이 있는 경우에만 가능합니다.'
    ]
  },
  {
    title: '4. 콘텐츠와 권리 책임',
    body: [
      '서비스는 사용자가 입력한 URL에 대해 공개적으로 접근 가능한 정보 또는 다운로드 가능한 파일을 있는 그대로 처리할 수 있습니다. 콘텐츠의 소유권, 합법성, 정확성, 다운로드 가능 여부를 보증하지 않습니다.',
      '사용자가 입력한 URL, 다운로드한 파일, 해당 파일의 이용으로 발생하는 법적 책임은 사용자에게 있습니다. 권리 침해 또는 남용이 확인되거나 합리적으로 의심되는 경우 서비스 제공을 제한할 수 있습니다.'
    ]
  },
  {
    title: '5. 서비스 자료의 이용',
    body: [
      '서비스 화면, 디자인, 자체 제작 이미지, 코드가 아닌 공개 표시 자료는 서비스 제공을 위해 제공됩니다. 사용자는 서비스 이용 목적을 넘어 이를 복제, 수정, 재배포, 미러링하거나 상업적으로 이용할 수 없습니다.',
      '단, 오픈소스 라이선스가 명시된 코드나 자료가 있는 경우 해당 라이선스가 우선합니다.'
    ]
  },
  {
    title: '6. 면책 및 책임 제한',
    body: [
      '서비스는 현재 상태 그대로 제공됩니다. 특정 플랫폼의 정책 변경, 네트워크 상태, 원본 콘텐츠 삭제, 접근 제한, 파일 형식 변경 등에 따라 다운로드가 실패하거나 품질이 달라질 수 있습니다.',
      '관련 법령이 허용하는 범위에서, 서비스 운영자는 사용자의 법령 위반, 제3자 권리 침해, 플랫폼 약관 위반, 다운로드 파일 사용 또는 서비스 이용 불가로 인한 손해에 대해 책임을 지지 않습니다.'
    ]
  },
  {
    title: '7. 변경 및 중단',
    body: [
      '서비스 기능, 지원 플랫폼, 다운로드 방식, 약관은 운영상 필요 또는 법령 변경에 따라 수정될 수 있습니다. 중요한 변경이 있는 경우 서비스 화면에 업데이트 일자를 반영합니다.',
      '보안, 남용 방지, 인프라 장애, 외부 플랫폼 변경이 있는 경우 사전 고지 없이 일부 기능이 제한되거나 중단될 수 있습니다.'
    ]
  }
];

const privacySections: LegalSection[] = [
  {
    title: '1. 기본 원칙',
    body: [
      '설계자x트래픽정 Video Downloader는 로그인, 회원가입, 계정 생성을 요구하지 않습니다. 별도의 사용자 데이터베이스를 만들어 이름, 이메일, 전화번호 같은 개인정보를 의도적으로 수집하거나 저장하지 않습니다.',
      '다만 서비스를 제공하고 보안을 유지하기 위해 인프라 제공자와 서버 로그에 기술 정보가 일시적으로 처리될 수 있습니다.'
    ]
  },
  {
    title: '2. 처리될 수 있는 정보',
    items: [
      '사용자가 입력한 URL: 링크 분석과 다운로드 전달을 위해 일시적으로 처리됩니다. 요청 URL에 포함된 값은 보안 로그나 오류 로그에 제한적으로 남을 수 있습니다.',
      '기술 정보: IP 주소, 브라우저 종류, 운영체제, 접속 시간, 요청 경로, 오류 정보, 사용자 에이전트 등 서비스 운영에 필요한 비식별 또는 기술 정보가 처리될 수 있습니다.',
      '다운로드 파일: 파일은 사용자의 요청에 따라 사용자의 기기로 전달되며, 운영자가 별도 저장소에 보관하거나 백업하지 않습니다.'
    ]
  },
  {
    title: '3. 이용 목적',
    items: [
      '사용자가 요청한 URL 분석, 미리보기 표시, 다운로드 파일 전달',
      '오류 해결, 보안 점검, 서비스 안정성 유지',
      '남용, 자동화 공격, 권리 침해 신고 또는 법령 위반 대응',
      '법적 의무 이행 또는 권리 보호'
    ]
  },
  {
    title: '4. 쿠키와 광고',
    body: [
      '현재 서비스는 로그인 추적용 쿠키나 맞춤형 광고 쿠키를 직접 설정하지 않습니다. 향후 광고, 분석 도구 또는 쿠키 기반 기능을 도입하는 경우 본 정책을 업데이트하고 필요한 고지를 제공합니다.',
      '제3자 플랫폼으로 이동하거나 외부 링크를 사용하는 경우 해당 사이트의 개인정보 처리방침과 쿠키 정책이 적용됩니다.'
    ]
  },
  {
    title: '5. 제3자 처리 및 국외 처리',
    body: [
      '서비스 배포, 보안, 네트워크 전달을 위해 Cloudflare 등 인프라 제공자가 기술 정보를 처리할 수 있습니다. 이러한 정보는 제공자의 글로벌 인프라에서 국외 처리될 수 있습니다.',
      '운영자는 사용자의 개인정보를 판매하지 않으며, 법령상 요구, 권리 보호, 보안 대응 또는 서비스 제공에 필요한 범위를 넘어 제3자에게 제공하지 않습니다.'
    ]
  },
  {
    title: '6. 보관 기간',
    body: [
      '사용자가 입력한 URL과 다운로드 파일은 서비스 제공 목적상 일시적으로 처리되며, 운영자가 별도 데이터베이스에 저장하지 않습니다.',
      '기술 로그는 보안, 장애 대응, 남용 방지를 위해 인프라 제공자 또는 운영 환경의 보관 정책에 따라 제한된 기간 보관될 수 있습니다.'
    ]
  },
  {
    title: '7. 이용자 권리',
    body: [
      '사용자는 적용 가능한 법령에 따라 본인 정보의 열람, 정정, 삭제, 처리 제한을 요청할 수 있습니다. 권리 침해 신고나 개인정보 문의가 있는 경우 운영자가 공개한 공식 연락 채널로 요청할 수 있습니다.',
      '서비스는 계정 기반 저장소를 운영하지 않으므로, 요청 처리 시 실제 저장된 정보가 없거나 기술 로그 범위로 제한될 수 있습니다.'
    ]
  },
  {
    title: '8. 아동의 개인정보',
    body: [
      '서비스는 아동의 개인정보를 의도적으로 수집하지 않습니다. 미성년자는 보호자의 동의와 관련 법령 및 플랫폼 약관이 허용하는 범위에서만 서비스를 사용해야 합니다.'
    ]
  },
  {
    title: '9. 정책 변경',
    body: [
      '개인정보 처리방침은 서비스 구조, 법령, 인프라 변경에 따라 업데이트될 수 있습니다. 변경 시 본 화면의 최종 업데이트 일자를 수정합니다.'
    ]
  }
];

const detectPlatform = (value: string): PlatformId | null => {
  const url = extractSupportedUrl(value).toLowerCase();
  if (/^https?:\/\/(?:www\.|mobile\.)?(?:x\.com|twitter\.com)\//i.test(url)) return 'x';

  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (
    url.includes('tiktok.com')
    || url.includes('vt.tiktok.com')
    || url.includes('vm.tiktok.com')
    || url.includes('douyin.com')
  ) return 'tiktok';
  if (url.includes('instagram.com')) return 'instagram';
  if (url.includes('threads.net') || url.includes('threads.com')) return 'threads';
  if (url.includes('facebook.com') || url.includes('fb.watch')) return 'facebook';
  if (url.includes('xhslink.com') || url.includes('xhslink.cn') || url.includes('xiaohongshu.com')) return 'xiaohongshu';
  if (url.includes('pinterest.') || url.includes('pin.it')) return 'pinterest';
  if (url.includes('aliexpress.')) return 'aliexpress';

  return null;
};

const extractSupportedUrl = (value: string) => {
  const candidates = value.match(/https?:\/\/[^\s<>"'`]+/gi) || [];

  for (const candidate of candidates) {
    const cleaned = cleanPastedUrl(candidate);
    const lower = cleaned.toLowerCase();

    if (
      lower.includes('youtube.com')
      || lower.includes('youtu.be')
      || lower.includes('tiktok.com')
      || lower.includes('douyin.com')
      || lower.includes('instagram.com')
      || lower.includes('threads.net')
      || lower.includes('threads.com')
      || lower.includes('facebook.com')
      || lower.includes('fb.watch')
      || lower.includes('xhslink.com')
      || lower.includes('xhslink.cn')
      || lower.includes('xiaohongshu.com')
      || lower.includes('pinterest.')
      || lower.includes('pin.it')
      || lower.includes('aliexpress.')
      || /^https?:\/\/(?:www\.|mobile\.)?(?:x\.com|twitter\.com)\//i.test(cleaned)
    ) {
      return cleaned;
    }
  }

  return cleanPastedUrl(value);
};

const cleanPastedUrl = (value: string) => value.trim().replace(/[),.;!?，。]+$/g, '');

const getFeatureIcon = (icon: Feature['icon']) => {
  switch (icon) {
    case 'hd':
      return <span className="hd-icon">HD</span>;
    case 'shield':
      return <ShieldCheck size={28} />;
    case 'sparkles':
      return <Sparkles size={28} />;
    case 'lock':
      return <Lock size={28} />;
    case 'phone':
      return <Smartphone size={28} />;
    case 'smile':
      return <Sparkles size={28} />;
    case 'drop':
      return <ShieldCheck size={28} />;
    default:
      return <Zap size={28} />;
  }
};

const getOptionIcon = (icon: DownloadOption['icon']) => {
  if (icon === 'image') return <ImageDown size={22} />;
  if (icon === 'music') return <Music size={22} />;
  if (icon === 'gif') return <LoaderCircle size={22} />;
  return <FileVideo size={22} />;
};

function App() {
  const [selectedId, setSelectedId] = useState<PlatformId>('youtube');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<'idle' | 'extracting' | 'ready' | 'downloading' | 'downloaded' | 'error'>(
    'idle'
  );
  const [activeDownload, setActiveDownload] = useState<string | null>(null);
  const [videoData, setVideoData] = useState<ExtractedVideo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [legalTab, setLegalTab] = useState<LegalTab | null>(null);

  const platform = platforms[selectedId];
  const detectedId = useMemo(() => detectPlatform(url), [url]);
  const displayPlatform = detectedId ? platforms[detectedId] : platform;

  useEffect(() => {
    if (legalTab) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      const frame = window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      });
      const timer = window.setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 0);
      const lateTimer = window.setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 80);

      return () => {
        window.cancelAnimationFrame(frame);
        window.clearTimeout(timer);
        window.clearTimeout(lateTimer);
      };
    }
  }, [legalTab]);

  const selectPlatform = (id: PlatformId) => {
    if (status === 'downloading' || status === 'extracting') return;
    setSelectedId(id);
    setUrl('');
    setStatus('idle');
    setVideoData(null);
    setErrorMessage('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const submittedUrl = url.trim();

    if (!submittedUrl || status === 'downloading' || status === 'extracting') {
      return;
    }

    const nextId = detectPlatform(submittedUrl) ?? selectedId;
    setSelectedId(nextId);
    setStatus('extracting');
    setVideoData(null);
    setErrorMessage('');

    try {
      const response = await fetch(apiUrl(`/api/extract?url=${encodeURIComponent(submittedUrl)}`));
      const payload = (await response.json()) as ExtractResponse;

      if (!response.ok || !payload.ok || !payload.data) {
        throw new Error(payload.error || '링크 분석에 실패했습니다.');
      }

      setSelectedId(payload.data.platform || nextId);
      setVideoData(payload.data);
      setStatus('ready');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : '링크 분석에 실패했습니다.');
    }
  };

  const handleDownload = async (option: DownloadOption) => {
    if (status === 'downloading') return;
    setStatus('downloading');
    setActiveDownload(option.id);
    setErrorMessage('');
    const sourceUrl = videoData?.originalUrl || url;
    const filename = `${videoData?.title || displayPlatform.contentTitle}-${option.id}.${extensionForOption(option)}`;
    if (option.directUrl && option.audioUrl) {
      try {
        const loadMedia = async (remote: string) => {
          const response = await fetch(directMediaUrl(remote, filename), { signal: AbortSignal.timeout(180000) });
          if (!response.ok || !/^(video|audio)\//.test(response.headers.get('content-type') || '')) {
            throw new Error('영상 파일을 받지 못했습니다. 링크를 다시 분석한 뒤 시도하세요.');
          }
          return response.blob();
        };
        const [video, audio, { mergeVideoAudio }] = await Promise.all([
          loadMedia(option.directUrl), loadMedia(option.audioUrl), import('./mux')
        ]);
        const merged = await mergeVideoAudio(video, audio);
        const blobUrl = URL.createObjectURL(merged);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename.replace(/[\\/:*?"<>|]/g, '_').slice(0, 180).replace(/\.mp4$/, '') + '.mp4';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        setStatus('downloaded');
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : '다운로드에 실패했습니다.');
        setStatus('error');
      } finally {
        setActiveDownload(null);
      }
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = option.directUrl
      ? directMediaUrl(option.directUrl, filename)
      : apiUrl(`/api/download?url=${encodeURIComponent(sourceUrl)}&format=${encodeURIComponent(option.id)}`);
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => {
      setStatus('downloaded');
      setActiveDownload(null);
    }, 1600);
  };

  const resultTitle = videoData?.title || displayPlatform.contentTitle;
  const resultAuthor = videoData?.author || displayPlatform.author;
  const resultDuration = videoData?.duration || displayPlatform.duration;
  const resultDate = videoData?.date || displayPlatform.date;
  const resultMeta = videoData
    ? [resultDuration, resultDate].filter(Boolean).join(' · ') || '링크 분석 완료'
    : displayPlatform.author;
  const resultOptions = videoData?.options?.length ? videoData.options : displayPlatform.options;
  const previewImageOption = resultOptions.find((option) => option.icon === 'image' && option.directUrl);
  const previewVideoOption = resultOptions.find((option) => option.icon === 'video' && option.directUrl);
  const resultPreviewImage = videoData?.thumbnail
    ? directMediaUrl(videoData.thumbnail, `${resultTitle}-thumbnail.jpg`, true)
    : previewImageOption?.directUrl
      ? directMediaUrl(
        previewImageOption.directUrl,
        `${resultTitle}-${previewImageOption.id}.${extensionForOption(previewImageOption)}`,
        true
      )
      : '';
  const resultPreviewVideo = !resultPreviewImage && previewVideoOption?.directUrl
    ? directMediaUrl(
      previewVideoOption.directUrl,
      `${resultTitle}-${previewVideoOption.id}.${extensionForOption(previewVideoOption)}`,
      true
    )
    : '';
  const resultPreview = resultPreviewImage || displayPlatform.icon;
  const hasMediaPreview = Boolean(resultPreviewImage || resultPreviewVideo);
  const canSubmit = url.trim().length > 0 && status !== 'extracting' && status !== 'downloading';
  const statusText =
    status === 'extracting'
      ? '분석 중'
      : status === 'downloading'
        ? '다운로드 준비 중'
        : status === 'downloaded'
          ? '저장 요청 완료'
          : status === 'error'
            ? '오류'
            : videoData
              ? (videoData.warning ? '일부 준비됨' : '준비됨')
              : '링크를 분석하세요';

  const openLegal = (tab: LegalTab, event?: MouseEvent<HTMLButtonElement>) => {
    event?.currentTarget.blur();
    setLegalTab(tab);
  };

  const closeLegal = () => {
    setLegalTab(null);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  if (legalTab) {
    const legalTitle =
      legalTab === 'terms' ? '서비스 이용약관' : legalTab === 'privacy' ? '개인정보 처리방침' : '문의 및 신고';
    const legalDescription =
      legalTab === 'terms'
        ? '설계자x트래픽정 Video Downloader 이용 조건과 사용자 책임을 정합니다.'
        : legalTab === 'privacy'
          ? '서비스 제공 과정에서 처리될 수 있는 정보와 이용자의 권리를 설명합니다.'
          : '권리 침해, 개인정보, 서비스 남용 관련 요청 안내입니다.';
    const legalSections = legalTab === 'terms' ? termsSections : privacySections;

    return (
      <main className="app platform-youtube legal-app" data-testid="app-shell">
        <header className="site-header">
          <a className="brand" href="/" aria-label="Video Downloader home" onClick={(event) => {
            event.preventDefault();
            closeLegal();
          }}>
            <img className="brand-mark" src={platforms.youtube.icon} alt="" />
            <span>Video Downloader</span>
          </a>
          <nav className="main-nav" aria-label="주요 메뉴">
            <button className="nav-button" type="button" onClick={closeLegal}>
              홈으로
            </button>
          </nav>
        </header>

        <section className="legal-page" data-testid="legal-page" aria-labelledby="legal-title">
          <div className="section-heading">
            <span>최종 업데이트: {updatedDate}</span>
            <h1 id="legal-title">{legalTitle}</h1>
            <p>{legalDescription}</p>
          </div>

          <div className="legal-tabs" role="tablist" aria-label="법적 고지">
            {legalTabs.map((tab) => (
              <button
                className={tab.id === legalTab ? 'legal-tab active' : 'legal-tab'}
                type="button"
                role="tab"
                aria-selected={tab.id === legalTab}
                key={tab.id}
                onClick={() => setLegalTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {legalTab === 'contact' ? (
            <article className="contact-notice legal-contact">
              <h2>문의 및 신고</h2>
              <p>
                권리 침해, 개인정보, 서비스 남용 관련 요청은 운영자가 공개한 공식 연락 채널로 보내주세요. 신고에는 문제가
                되는 URL, 권리 관계, 요청 내용을 함께 적어야 빠르게 확인할 수 있습니다.
              </p>
            </article>
          ) : (
            <div className="legal-list">
              {legalSections.map((section) => (
                <article className="legal-card" key={section.title}>
                  <h2>{section.title}</h2>
                  {section.body?.map((text) => (
                    <p key={text}>{text}</p>
                  ))}
                  {section.items && (
                    <ul>
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        <footer className="site-footer">
          <span>© 2026 Video Downloader. All rights reserved.</span>
          <nav aria-label="푸터 메뉴">
            <button className="footer-link" type="button" onClick={() => setLegalTab('terms')}>
              이용약관
            </button>
            <button className="footer-link" type="button" onClick={() => setLegalTab('privacy')}>
              개인정보처리방침
            </button>
            <button className="footer-link" type="button" onClick={() => setLegalTab('contact')}>
              문의하기
            </button>
          </nav>
        </footer>
      </main>
    );
  }

  return (
    <main className={`app platform-${displayPlatform.id}`} data-testid="app-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="Video Downloader home">
          <img className="brand-mark" src={displayPlatform.icon} alt="" />
          <span>{displayPlatform.brand}</span>
        </a>
        <nav className="main-nav" aria-label="주요 메뉴">
          <a href="#features">기능</a>
          <a href="#guide">가이드</a>
          <a href="#faq">FAQ</a>
          <button className="language-button" type="button">
            KO
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </nav>
      </header>

      <section className="hero" aria-labelledby="hero-title">
        <div className="platform-tabs" aria-label="플랫폼 선택">
          {platformList.map((item) => (
            <button
              className={item.id === displayPlatform.id ? 'platform-tab active' : 'platform-tab'}
              type="button"
              key={item.id}
              onClick={() => selectPlatform(item.id)}
              disabled={status === 'extracting' || status === 'downloading'}
              data-testid={`tab-${item.id}`}
            >
              <img className="platform-tab-icon" src={item.icon} alt="" />
              {item.label}
            </button>
          ))}
        </div>

        <h1 id="hero-title">{displayPlatform.h1}</h1>
        <p>{displayPlatform.subtitle}</p>

        <form className="download-form" onSubmit={handleSubmit}>
          <label className="url-field">
            <span className="sr-only">동영상 URL</span>
            <Link size={22} aria-hidden="true" />
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder={displayPlatform.placeholder}
              data-testid="url-input"
              disabled={status === 'extracting' || status === 'downloading'}
            />
          </label>
          <button className="primary-button" type="submit" data-testid="submit-download" disabled={!canSubmit}>
            {status === 'extracting' ? <LoaderCircle size={22} className="spin" aria-hidden="true" /> : <Download size={22} aria-hidden="true" />}
            {status === 'extracting' ? '분석 중' : '다운로드'}
          </button>
        </form>
        {errorMessage && (
          <div className="status-message error" data-testid="extract-error">
            {errorMessage}
          </div>
        )}
        {videoData?.warning && (
          <div className="status-message warning" role="status" data-testid="extract-warning">
            {videoData.warning}
          </div>
        )}
      </section>

      <section className="result-section" aria-label="다운로드 결과">
        <article className="result-card" data-testid="result-card">
          <div className={`preview-frame ${displayPlatform.orientation}${hasMediaPreview ? '' : ' brand-preview'}`}>
            {resultPreviewVideo ? (
              <video
                src={resultPreviewVideo}
                controls
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                onLoadedMetadata={(event) => {
                  const preview = event.currentTarget;

                  if (Number.isFinite(preview.duration) && preview.duration > 1) {
                    preview.currentTime = Math.min(1, preview.duration / 3);
                  }
                }}
              />
            ) : (
              <img src={resultPreview} alt={`${displayPlatform.label} 미리보기`} />
            )}
            {videoData && !resultPreviewVideo && (
              <button className="play-button" type="button" aria-label="미리보기 재생">
                <Video size={34} fill="currentColor" />
              </button>
            )}
            {resultDuration && <span className="duration">{resultDuration}</span>}
          </div>

          <div className="result-detail">
            <div className="title-row">
              <h2>{resultTitle}</h2>
            </div>
            <div className="author-row">
              <div className="avatar">{resultAuthor.slice(0, 1)}</div>
              <div className={videoData ? 'author-copy' : 'author-copy creator-copy'}>
                <div className="author-name">
                  {resultAuthor}
                  {!videoData && displayPlatform.verified && <BadgeCheck size={17} fill="currentColor" />}
                </div>
                <span>{resultMeta}</span>
              </div>
              <a className="open-chat-link" href={OPEN_CHAT_URL}>
                오픈챗참여
              </a>
            </div>

            {videoData && (
              <>
                <div className="format-header">
                  <span>다운로드 형식 선택</span>
                  <small>{statusText}</small>
                </div>

                <div className="format-list">
                  {resultOptions.map((option) => (
                    <button
                      className="format-row"
                      type="button"
                      key={option.id}
                      onClick={() => handleDownload(option)}
                      disabled={status === 'extracting' || status === 'downloading'}
                      data-testid={`download-${displayPlatform.id}-${option.id}`}
                    >
                      <span className="format-icon">{getOptionIcon(option.icon)}</span>
                      <strong>{option.kind}</strong>
                      <span>{option.label}</span>
                      <em>{option.detail}</em>
                      <span className="row-download">
                        {activeDownload === option.id ? <LoaderCircle size={18} className="spin" /> : <Download size={18} />}
                        다운로드
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </article>
      </section>

      <section className="feature-grid" id="features" aria-label="기능">
        {displayPlatform.features.map((feature) => (
          <article className="mini-card" key={feature.title}>
            <div className="icon-badge">{getFeatureIcon(feature.icon)}</div>
            <div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="guide" id="guide" aria-label="사용 방법">
        <h2>사용 방법</h2>
        <div className="step-grid">
          {displayPlatform.steps.map((step, index) => (
            <article className="step-card" key={step}>
              <span>{index + 1}</span>
              <div>
                <h3>{index === 0 ? '링크 복사' : index === 1 ? '붙여넣기' : '다운로드'}</h3>
                <p>{step}</p>
              </div>
              {index < displayPlatform.steps.length - 1 && <ChevronRight className="step-arrow" size={34} />}
            </article>
          ))}
        </div>
      </section>

      <section className="faq" id="faq" aria-label="FAQ">
        <h2>FAQ</h2>
        <div className="faq-grid">
          <article>
            <h3>로그인이 필요한가요?</h3>
            <p>아니요. 이 화면의 테스트 다운로드 흐름은 계정 로그인 없이 바로 동작합니다.</p>
          </article>
          <article>
            <h3>어떤 링크를 넣을 수 있나요?</h3>
            <p>유튜브, 틱톡·Douyin, 샤오홍슈, 핀터레스트, 인스타그램, 스레드, 페이스북, X, 알리익스프레스 URL을 감지해 해당 화면으로 전환합니다. 1688·타오바오·티몰 상품은 사이트 로그인이 필요해 지원하지 않습니다.</p>
          </article>
        </div>
      </section>

      <footer className="site-footer">
        <span>© 2026 Video Downloader. All rights reserved.</span>
        <nav aria-label="푸터 메뉴">
          <button className="footer-link" type="button" onClick={(event) => openLegal('terms', event)}>
            이용약관
          </button>
          <button className="footer-link" type="button" onClick={(event) => openLegal('privacy', event)}>
            개인정보처리방침
          </button>
          <button className="footer-link" type="button" onClick={(event) => openLegal('contact', event)}>
            문의하기
          </button>
        </nav>
      </footer>
    </main>
  );
}

export default App;
